import moment from 'moment';

/**
 * FE#260 — pure helpers behind the two fixes to the campaign wizard's
 * "Schedule for later" TIME field.
 *
 * (1) `resolveDisplayTime` fixes the precedence bug: the displayed time must
 *     follow the LIVE `dueDate`. A saved `defaultSchedulingTime` preference may
 *     only SEED the value when there is no dueDate yet — the old code returned
 *     the fixed preference and ignored `dueDate`, so a user with a default time
 *     set could never change the field (edits didn't stick).
 *
 * (2) `formatTimeTyping` / `correctTime` / `isCompleteValidTime` back the plain
 *     validated input that replaces `react-input-mask@2.0.4` (a version that is
 *     not React-18-compatible and stops accepting controlled input).
 */

const HH_MM = /^([01]?\d|2[0-3]):[0-5]\d$/;

/** month=… not relevant here; format a stored ISO to local HH:mm, or '' if unusable. */
const dueDateToHHmm = (dueDate?: string): string => {
  if (!dueDate) return '';
  const m = moment.utc(dueDate);
  if (!m.isValid()) return '';
  return m.local().format('HH:mm');
};

/** Normalize a loose HH:mm-ish preference to a zero-padded, in-range HH:mm, or '' . */
const normalizePreference = (pref?: string): string => {
  if (!pref || pref === 'current time') return '';
  const match = pref.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return '';
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return '';
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * The HH:mm to DISPLAY in the time field. Live `dueDate` wins; the saved
 * preference only seeds the value when there is no dueDate yet.
 */
export const resolveDisplayTime = (dueDate?: string, defaultSchedulingTime?: string): string => {
  const fromDueDate = dueDateToHHmm(dueDate);
  if (fromDueDate) return fromDueDate;
  return normalizePreference(defaultSchedulingTime);
};

/**
 * Format characters typed into the field into an in-progress HH:mm, replicating
 * the old `mask="99:99"`: keep up to 4 digits, insert ':' after the 2nd.
 */
export const formatTimeTyping = (raw: string): string => {
  const digits = (raw ?? '').replace(/\D/g, '').slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
};

/** Clamp a (possibly out-of-range) HH:mm to a valid 24h time, zero-padded. */
export const correctTime = (timeString: string): string => {
  const parts = (timeString ?? '').split(':');
  if (parts.length !== 2) return timeString;
  let [hours, minutes] = parts.map(Number);
  if (isNaN(hours) || hours < 0) hours = 0;
  else if (hours > 23) hours = 23;
  if (isNaN(minutes) || minutes < 0) minutes = 0;
  else if (minutes > 59) minutes = 59;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Read a time the way a person types it — "9:47", "947", "21:47", "9.47",
 * "9:47pm", "9" — into a zero-padded 24h HH:mm, or null when it is not a time.
 *
 * In a 12h locale a bare 1–12 hour with no am/pm keeps the half of the day the
 * current value is in (`referenceHHmm`): someone at 9:45 PM typing "9:47"
 * means 21:47, not the morning.
 */
export const parseTypedTime = (
  raw: string,
  { uses12h, referenceHHmm }: { uses12h: boolean; referenceHHmm?: string },
): string | null => {
  const text = (raw ?? '').toLowerCase().replace(/\s+/g, '')
    // A mask still waiting for its last digit ("21:3", from "213") is read as
    // the three digits it came from: 2:13.
    .replace(/^(\d{2}):(\d)(?=a|am|p|pm|$)/, '$1$2');
  const m = /^(\d{1,4})(?:[:.](\d{2}))?(a|am|p|pm)?$/.exec(text);
  if (!m) return null;

  let hours: number;
  let minutes: number;
  if (m[2] !== undefined) {
    if (m[1].length > 2) return null;
    hours = +m[1];
    minutes = +m[2];
  } else if (m[1].length <= 2) {
    hours = +m[1];
    minutes = 0;
  } else {
    hours = +m[1].slice(0, -2);
    minutes = +m[1].slice(-2);
  }
  if (minutes > 59) return null;

  const meridiem = m[3]?.[0];
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    hours = (hours % 12) + (meridiem === 'p' ? 12 : 0);
  } else if (uses12h && hours >= 1 && hours < 12) {
    const refHour = Number((referenceHHmm ?? '').split(':')[0]);
    if (refHour >= 12) hours += 12;
  }
  if (hours > 23) return null;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * The mask for the type-a-time field: the ":" appears on its own as digits are
 * typed — "2130" → "21:30", "947" → "9:47" (94 is no hour, so the colon goes
 * after the 9). A colon the person typed themselves is respected, and an am/pm
 * tail is kept, upper-cased ("947p" → "9:47 P").
 */
export const maskTypedTime = (raw: string): string => {
  const text = raw ?? '';
  const letters = text.replace(/[^apm]/gi, '').slice(0, 2).toUpperCase();
  const suffix = letters ? ` ${letters}` : '';

  const sep = /[:.]/.exec(text);
  if (sep) {
    const hours = text.slice(0, sep.index).replace(/\D/g, '').slice(0, 2);
    const minutes = text.slice(sep.index + 1).replace(/\D/g, '').slice(0, 2);
    return `${hours}:${minutes}${suffix}`;
  }

  const d = text.replace(/\D/g, '').slice(0, 4);
  let masked = d;
  if (d.length === 3) masked = +d.slice(0, 2) <= 23 ? `${d.slice(0, 2)}:${d[2]}` : `${d[0]}:${d.slice(1)}`;
  else if (d.length === 4) masked = `${d.slice(0, 2)}:${d.slice(2)}`;
  return `${masked}${suffix}`;
};

/** True only for a complete, in-range HH:mm (single-digit hour allowed). */
export const isCompleteValidTime = (timeString: string): boolean => HH_MM.test(timeString ?? '');
